<template>
  <transition name="pwa-fade">
    <div v-if="visible" class="pwa-install-banner" role="dialog" aria-label="Install app">
      <div class="pwa-install-banner__content">
        <div class="pwa-install-banner__icon" aria-hidden="true">
          <i class="bx bx-mobile-alt"></i>
        </div>
        <div class="pwa-install-banner__text">
          <strong>Install StaffSync</strong>
          <span>{{ installHint }}</span>
        </div>
      </div>
      <div class="pwa-install-banner__actions">
        <button type="button" class="pwa-btn pwa-btn--ghost" @click="dismiss">Not now</button>
        <button v-if="canNativeInstall" type="button" class="pwa-btn pwa-btn--primary" @click="install">
          Install
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
const DISMISS_KEY = 'staffsync.pwa.install.dismissed';

export default {
  name: 'PwaInstallPrompt',
  data() {
    return {
      visible: false,
      deferredPrompt: null,
      isIos: false,
      isStandalone: false
    };
  },
  computed: {
    canNativeInstall() {
      return !!this.deferredPrompt;
    },
    installHint() {
      if (this.isStandalone) return '';
      if (this.isIos) return 'Tap Share, then Add to Home Screen';
      if (this.canNativeInstall) return 'Add to your home screen for quick access';
      return 'Use your browser menu to install this app';
    }
  },
  mounted() {
    this.isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    const ua = window.navigator.userAgent || '';
    this.isIos = /iphone|ipad|ipod/i.test(ua);

    if (this.isStandalone || sessionStorage.getItem(DISMISS_KEY) === '1') {
      return;
    }

    window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);

    if (this.isIos || this.isMobileViewport()) {
      setTimeout(() => {
        if (!this.isStandalone) this.visible = true;
      }, 2500);
    }
  },
  beforeUnmount() {
    window.removeEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
  },
  methods: {
    isMobileViewport() {
      return window.matchMedia('(max-width: 768px)').matches;
    },
    onBeforeInstallPrompt(event) {
      event.preventDefault();
      this.deferredPrompt = event;
      if (!sessionStorage.getItem(DISMISS_KEY)) {
        this.visible = true;
      }
    },
    async install() {
      if (!this.deferredPrompt) {
        this.dismiss();
        return;
      }
      this.deferredPrompt.prompt();
      await this.deferredPrompt.userChoice;
      this.deferredPrompt = null;
      this.visible = false;
    },
    dismiss() {
      sessionStorage.setItem(DISMISS_KEY, '1');
      this.visible = false;
    }
  }
};
</script>

<style scoped>
.pwa-install-banner {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  z-index: 10050;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pwa-install-banner__content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pwa-install-banner__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #eef2ff;
  color: #4f46e5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.pwa-install-banner__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  color: #64748b;
}

.pwa-install-banner__text strong {
  color: #0f172a;
  font-size: 15px;
}

.pwa-install-banner__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.pwa-btn {
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
}

.pwa-btn--ghost {
  background: #f1f5f9;
  color: #475569;
}

.pwa-btn--primary {
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: #fff;
}

.pwa-fade-enter-active,
.pwa-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.pwa-fade-enter-from,
.pwa-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (min-width: 769px) {
  .pwa-install-banner {
    left: auto;
    right: 20px;
    bottom: 20px;
    max-width: 380px;
  }
}
</style>
